import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import axios from 'axios';
import Header from '../components/layout/Header';
import Footer from '../components/layout/Footer';
import CartDrawer from '../components/cart/CartDrawer';
import { useCart } from '../context/CartContext';
import { SHIPPING_NOTICE, SHIPPING_COUNTRIES, CONTACT_INFO, BRAND_INFO, CURRENCY, COUNTRY_OF_ORIGIN, SHIPPING_INFO, ACCEPTED_CARDS_LABEL } from '../data/mock';
import { Lock, Truck, ArrowLeft, AlertCircle, ShoppingBag, Shield, Phone, CheckCircle, CreditCard, Mail } from 'lucide-react';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import PaymentIcons from '../components/PaymentIcons';
import usePageTitle from '../hooks/usePageTitle';

// ---------------------------------------------------------------------------
// Public/client-safe configuration with fallbacks.
//
// These values are *public* by design and end up in the browser bundle either
// way. The hardcoded values below act as a fallback for deployments where the
// build environment doesn't have the matching .env / REACT_APP_* variables
// (e.g. Hostinger static hosting without a build-time env config). When the
// env var IS set, it takes precedence.
//
// SAFE TO COMMIT:
//   - BACKEND_URL: client-side API host (visible in every API request).
//   - NMI_TOKENIZATION_KEY: publishable key for Collect.js (NMI's design
//     calls for this to be embedded in the browser; the matching Security
//     Key lives only in the backend).
// NEVER PUT HERE: NMI Security Key, MongoDB URL, any server secret.
// ---------------------------------------------------------------------------
const BACKEND_URL =
  process.env.REACT_APP_BACKEND_URL ||
  'https://quilting-gear-hub.preview.emergentagent.com';
const NMI_TOKENIZATION_KEY =
  process.env.REACT_APP_NMI_TOKENIZATION_KEY ||
  'YQGcP9-JqP3XM-W9PKje-4awVP7';
const COLLECT_JS_URL = 'https://secure.nmi.com/token/Collect.js';

const CheckoutPage = () => {
  usePageTitle('Checkout', 'Secure checkout at Stitch Your Dash. Free shipping on orders over $99. Ships to USA only.');

  const navigate = useNavigate();
  const { cartItems, cartTotal, clearCart } = useCart();

  // UI state
  const [isProcessing, setIsProcessing] = useState(false);
  const [selectedCountry, setSelectedCountry] = useState('US');
  const [countryError, setCountryError] = useState(false);
  const [termsAccepted, setTermsAccepted] = useState(false);
  const [termsError, setTermsError] = useState(false);
  const [billingSameAsShipping, setBillingSameAsShipping] = useState(true);
  const [billingCountry, setBillingCountry] = useState('US');
  const [paymentError, setPaymentError] = useState('');
  const [collectJsReady, setCollectJsReady] = useState(false);

  // Form field state (we manage these in React so we can pass to backend after tokenization)
  const [contact, setContact] = useState({ email: '', phone: '' });
  const [shipping, setShipping] = useState({
    firstName: '', lastName: '', address1: '', address2: '', city: '', state: '', zip: '',
  });
  const [billing, setBilling] = useState({
    firstName: '', lastName: '', address1: '', address2: '', city: '', state: '', zip: '',
  });

  // We keep a ref to the latest form snapshot so the (memoized) Collect.js callback
  // can read it without being recreated on every keystroke.
  const formSnapshotRef = useRef({});

  const shippingCost = cartTotal >= 99 ? 0 : 9.95;
  const tax = cartTotal * 0.0825;
  const total = cartTotal + shippingCost + tax;

  // ---------------------------------------------------------------------------
  // Submit handler for the NMI sale (called from CollectJS callback)
  // ---------------------------------------------------------------------------
  const submitSaleToBackend = useCallback(async (paymentToken) => {
    const snapshot = formSnapshotRef.current;
    const effectiveBilling = snapshot.billingSameAsShipping ? snapshot.shipping : snapshot.billing;
    const effectiveBillingCountry = snapshot.billingSameAsShipping ? snapshot.selectedCountry : snapshot.billingCountry;

    const payload = {
      payment_token: paymentToken,
      amount: Number(snapshot.total.toFixed(2)),
      currency: 'USD',
      email: snapshot.contact.email || '',
      phone: snapshot.contact.phone || '',
      order_description: `${BRAND_INFO.name} order (${snapshot.itemCount} item${snapshot.itemCount === 1 ? '' : 's'})`,
      billing: {
        first_name: effectiveBilling.firstName || '',
        last_name: effectiveBilling.lastName || '',
        address1: effectiveBilling.address1 || '',
        address2: effectiveBilling.address2 || '',
        city: effectiveBilling.city || '',
        state: effectiveBilling.state || '',
        zip: effectiveBilling.zip || '',
        country: effectiveBillingCountry || 'US',
      },
      shipping: {
        first_name: snapshot.shipping.firstName || '',
        last_name: snapshot.shipping.lastName || '',
        address1: snapshot.shipping.address1 || '',
        address2: snapshot.shipping.address2 || '',
        city: snapshot.shipping.city || '',
        state: snapshot.shipping.state || '',
        zip: snapshot.shipping.zip || '',
        country: snapshot.selectedCountry || 'US',
      },
    };

    try {
      const { data } = await axios.post(`${BACKEND_URL}/api/checkout/nmi-sale`, payload, {
        headers: { 'Content-Type': 'application/json' },
        timeout: 45000,
      });

      if (data && data.success) {
        clearCart();
        navigate('/order-confirmation', {
          state: {
            success: true,
            transactionId: data.transaction_id,
            authCode: data.auth_code,
            responseCode: data.response_code,
            responseText: data.response_text,
            orderId: data.order_id,
            amount: snapshot.total,
            email: snapshot.contact.email,
          },
        });
      } else {
        // Navigate to the result page with the decline details so the user
        // sees a proper status screen (instead of just an inline error).
        // We intentionally DO NOT clearCart() so they can retry with a
        // different card.
        navigate('/order-confirmation', {
          state: {
            success: false,
            transactionId: data?.transaction_id || null,
            authCode: data?.auth_code || null,
            responseCode: data?.response_code || null,
            responseText: data?.response_text || data?.message || 'Payment was declined',
            orderId: data?.order_id || null,
            amount: snapshot.total,
            email: snapshot.contact.email,
          },
        });
        setIsProcessing(false);
      }
    } catch (err) {
      const msg = err?.response?.data?.detail || err?.message || 'Network error while processing payment.';
      setPaymentError(`Payment error: ${msg}`);
      setIsProcessing(false);
    }
  }, [clearCart, navigate]);

  // ---------------------------------------------------------------------------
  // Load Collect.js once and configure it
  // ---------------------------------------------------------------------------
  useEffect(() => {
    if (!NMI_TOKENIZATION_KEY) {
      setPaymentError('Payment configuration is missing. Please contact support.');
      return;
    }

    const configureCollectJs = () => {
      if (!window.CollectJS) return;
      try {
        window.CollectJS.configure({
          variant: 'inline',
          styleSniffer: false,
          tokenizationKey: NMI_TOKENIZATION_KEY,
          fields: {
            ccnumber: {
              selector: '#ccnumber',
              title: 'Card Number',
              placeholder: '1234 5678 9012 3456',
            },
            ccexp: {
              selector: '#ccexp',
              title: 'Card Expiration',
              placeholder: 'MM / YY',
            },
            cvv: {
              selector: '#cvv',
              title: 'CVV',
              placeholder: '123',
            },
          },
          customCss: {
            'font-family': 'Inter, sans-serif',
            'font-size': '15px',
            color: '#3f2a1d',
            'background-color': '#ffffff',
          },
          invalidCss: {
            color: '#b91c1c',
          },
          validCss: {
            color: '#3f2a1d',
          },
          placeholderCss: {
            color: '#a98a72',
          },
          focusCss: {
            outline: 'none',
          },
          fieldsAvailableCallback: () => {
            setCollectJsReady(true);
          },
          validationCallback: (field, status, message) => {
            // Optional: per-field validation feedback. We rely on overall callback for submit.
            if (!status && message) {
              setPaymentError(`${field}: ${message}`);
            }
          },
          timeoutDuration: 10000,
          timeoutCallback: () => {
            setPaymentError('Payment is taking too long. Please check your card details and try again.');
            setIsProcessing(false);
          },
          callback: (response) => {
            // response.token contains the one-time payment token
            const token = response && (response.token || response.payment_token);
            if (!token) {
              setPaymentError('Unable to tokenize card. Please re-check your card details.');
              setIsProcessing(false);
              return;
            }
            submitSaleToBackend(token);
          },
        });
      } catch (e) {
        // eslint-disable-next-line no-console
        console.error('CollectJS configure error:', e);
        setPaymentError('Could not initialize secure card fields. Please refresh the page.');
      }
    };

    // If already loaded (e.g. user navigated back), just configure
    if (window.CollectJS) {
      configureCollectJs();
      return;
    }

    // Otherwise inject the script tag
    const existing = document.querySelector(`script[src="${COLLECT_JS_URL}"]`);
    if (existing) {
      existing.addEventListener('load', configureCollectJs);
      return () => existing.removeEventListener('load', configureCollectJs);
    }

    const script = document.createElement('script');
    script.src = COLLECT_JS_URL;
    script.async = true;
    script.setAttribute('data-tokenization-key', NMI_TOKENIZATION_KEY);
    script.setAttribute('data-variant', 'inline');
    script.onload = configureCollectJs;
    script.onerror = () => {
      setPaymentError('Could not load secure payment library. Please check your connection and refresh.');
    };
    document.head.appendChild(script);

    return () => {
      // Don't remove script tag on unmount — Collect.js manages its own iframes and
      // reloading repeatedly causes issues.
    };
  }, [submitSaleToBackend]);

  // Keep the form snapshot ref in sync so the CollectJS callback always sees latest values
  useEffect(() => {
    formSnapshotRef.current = {
      contact,
      shipping,
      billing,
      billingSameAsShipping,
      selectedCountry,
      billingCountry,
      total,
      itemCount: cartItems.reduce((acc, i) => acc + (i.quantity || 1), 0),
    };
  }, [contact, shipping, billing, billingSameAsShipping, selectedCountry, billingCountry, total, cartItems]);

  // ---------------------------------------------------------------------------
  // Handlers
  // ---------------------------------------------------------------------------
  const handleCountryChange = (e) => {
    const country = e.target.value;
    setSelectedCountry(country);
    setCountryError(!SHIPPING_COUNTRIES.includes(country));
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    setPaymentError('');

    if (!SHIPPING_COUNTRIES.includes(selectedCountry)) {
      setCountryError(true);
      return;
    }
    if (!termsAccepted) {
      setTermsError(true);
      return;
    }
    if (!window.CollectJS) {
      setPaymentError('Payment library not loaded yet. Please wait a moment and try again.');
      return;
    }
    if (!collectJsReady) {
      setPaymentError('Card fields are still loading. Please wait a moment and try again.');
      return;
    }

    setIsProcessing(true);
    // Hand off to Collect.js: it will validate card fields, tokenize, and invoke our callback.
    try {
      window.CollectJS.startPaymentRequest();
    } catch (err) {
      // eslint-disable-next-line no-console
      console.error('CollectJS startPaymentRequest error:', err);
      setPaymentError('Could not start secure payment. Please try again.');
      setIsProcessing(false);
    }
  };

  // ---------------------------------------------------------------------------
  // Empty cart screen
  // ---------------------------------------------------------------------------
  if (cartItems.length === 0) {
    return (
      <div className="min-h-screen bg-warmWhite-100">
        <Header />
        <main className="pt-[80px] md:pt-[125px] lg:pt-[140px] pb-20">
          <div className="max-w-lg mx-auto px-4 text-center">
            <div className="w-24 h-24 bg-rose-100 rounded-full flex items-center justify-center mx-auto mb-6">
              <ShoppingBag className="w-10 h-10 text-rose-500" />
            </div>
            <h1 className="font-display text-2xl text-warmBrown-900 mb-4">Your Cart is Empty</h1>
            <p className="text-warmBrown-500 mb-8">Add items to proceed with checkout.</p>
            <Link to="/shop">
              <button className="px-8 py-4 bg-rose-600 hover:bg-rose-700 text-white font-semibold rounded-2xl transition-colors shadow-lg shadow-rose-600/20">
                Browse Products
              </button>
            </Link>
          </div>
        </main>
        <Footer />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-warmWhite-100">
      <Header />
      <CartDrawer />

      <main className="pt-[80px] md:pt-[120px] lg:pt-[136px] pb-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <Link
            to="/shop"
            className="inline-flex items-center gap-2 text-warmBrown-500 hover:text-rose-600 text-sm mb-6 transition-colors"
          >
            <ArrowLeft className="w-4 h-4" />
            Continue Shopping
          </Link>

          {/* Shipping & Currency Notice */}
          <div className="mb-8 p-4 bg-rose-50 border border-rose-100 rounded-2xl">
            <div className="flex flex-col sm:flex-row items-center justify-center gap-3 text-sm">
              <div className="flex items-center gap-2">
                <Truck className="w-5 h-5 text-rose-500" />
                <span className="text-rose-700 font-medium">{SHIPPING_NOTICE}</span>
              </div>
              <span className="hidden sm:inline text-rose-300">|</span>
              <span className="text-rose-600">All prices in {CURRENCY}</span>
              <span className="hidden sm:inline text-rose-300">|</span>
              <span className="text-rose-600">Ships from {COUNTRY_OF_ORIGIN}</span>
            </div>
          </div>

          <div className="grid lg:grid-cols-3 gap-8">
            {/* Form */}
            <div className="lg:col-span-2 space-y-6">
              <h1 className="font-display text-2xl text-warmBrown-900">Checkout</h1>

              <form onSubmit={handleSubmit} className="space-y-6">
                {/* Contact Info */}
                <div className="bg-white p-6 md:p-8 rounded-3xl shadow-card border border-cream-200">
                  <h2 className="font-display text-lg text-warmBrown-900 mb-6">Contact Information</h2>
                  <div className="grid md:grid-cols-2 gap-5">
                    <div>
                      <Label htmlFor="email" className="text-sm font-medium text-warmBrown-700">Email</Label>
                      <Input
                        id="email"
                        type="email"
                        value={contact.email}
                        onChange={(e) => setContact({ ...contact, email: e.target.value })}
                        className="mt-2 rounded-2xl border-cream-200 focus:ring-rose-400 focus:border-rose-400"
                        placeholder="your@email.com"
                        required
                      />
                    </div>
                    <div>
                      <Label htmlFor="phone" className="text-sm font-medium text-warmBrown-700">Phone</Label>
                      <Input
                        id="phone"
                        type="tel"
                        value={contact.phone}
                        onChange={(e) => setContact({ ...contact, phone: e.target.value })}
                        className="mt-2 rounded-2xl border-cream-200 focus:ring-rose-400 focus:border-rose-400"
                        placeholder="(555) 555-5555"
                      />
                    </div>
                  </div>
                </div>

                {/* Shipping Address */}
                <div className="bg-white p-6 md:p-8 rounded-3xl shadow-card border border-cream-200">
                  <h2 className="font-display text-lg text-warmBrown-900 mb-2">Shipping Address</h2>
                  <p className="text-xs text-warmBrown-500 mb-6">
                    The address where you&apos;d like your order delivered.
                  </p>
                  <div className="space-y-5">
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <Label className="text-sm font-medium text-warmBrown-700">First Name</Label>
                        <Input
                          value={shipping.firstName}
                          onChange={(e) => setShipping({ ...shipping, firstName: e.target.value })}
                          className="mt-2 rounded-2xl border-cream-200 focus:ring-rose-400 focus:border-rose-400"
                          required
                        />
                      </div>
                      <div>
                        <Label className="text-sm font-medium text-warmBrown-700">Last Name</Label>
                        <Input
                          value={shipping.lastName}
                          onChange={(e) => setShipping({ ...shipping, lastName: e.target.value })}
                          className="mt-2 rounded-2xl border-cream-200 focus:ring-rose-400 focus:border-rose-400"
                          required
                        />
                      </div>
                    </div>
                    <div>
                      <Label className="text-sm font-medium text-warmBrown-700">Country</Label>
                      <select
                        value={selectedCountry}
                        onChange={handleCountryChange}
                        className={`mt-2 w-full px-4 py-3 rounded-2xl border focus:outline-none focus:ring-2 transition-all ${
                          countryError
                            ? 'border-red-500 focus:ring-red-400'
                            : 'border-cream-200 focus:ring-rose-400 focus:border-rose-400'
                        }`}
                        required
                      >
                        <option value="US">United States</option>
                        <option value="OTHER">Other</option>
                      </select>
                      {countryError && (
                        <div className="mt-2 flex items-center gap-2 text-red-600 text-sm">
                          <AlertCircle className="w-4 h-4" />
                          <span>Sorry, we only ship within the United States</span>
                        </div>
                      )}
                    </div>
                    <div>
                      <Label className="text-sm font-medium text-warmBrown-700">Street Address</Label>
                      <Input
                        value={shipping.address1}
                        onChange={(e) => setShipping({ ...shipping, address1: e.target.value })}
                        className="mt-2 rounded-2xl border-cream-200 focus:ring-rose-400 focus:border-rose-400"
                        placeholder="123 Main Street"
                        required
                      />
                    </div>
                    <div>
                      <Label className="text-sm font-medium text-warmBrown-700">
                        Apartment, Suite, Unit <span className="text-warmBrown-400">(optional)</span>
                      </Label>
                      <Input
                        value={shipping.address2}
                        onChange={(e) => setShipping({ ...shipping, address2: e.target.value })}
                        className="mt-2 rounded-2xl border-cream-200 focus:ring-rose-400 focus:border-rose-400"
                        placeholder="Apt 4B"
                      />
                    </div>
                    <div className="grid grid-cols-3 gap-4">
                      <div>
                        <Label className="text-sm font-medium text-warmBrown-700">City</Label>
                        <Input
                          value={shipping.city}
                          onChange={(e) => setShipping({ ...shipping, city: e.target.value })}
                          className="mt-2 rounded-2xl border-cream-200 focus:ring-rose-400 focus:border-rose-400"
                          required
                        />
                      </div>
                      <div>
                        <Label className="text-sm font-medium text-warmBrown-700">State</Label>
                        <Input
                          value={shipping.state}
                          onChange={(e) => setShipping({ ...shipping, state: e.target.value })}
                          className="mt-2 rounded-2xl border-cream-200 focus:ring-rose-400 focus:border-rose-400"
                          required
                        />
                      </div>
                      <div>
                        <Label className="text-sm font-medium text-warmBrown-700">ZIP</Label>
                        <Input
                          value={shipping.zip}
                          onChange={(e) => setShipping({ ...shipping, zip: e.target.value })}
                          className="mt-2 rounded-2xl border-cream-200 focus:ring-rose-400 focus:border-rose-400"
                          required
                        />
                      </div>
                    </div>
                  </div>
                </div>

                {/* Billing Address */}
                <div className="bg-white p-6 md:p-8 rounded-3xl shadow-card border border-cream-200">
                  <div className="flex items-center justify-between mb-6 flex-wrap gap-3">
                    <h2 className="font-display text-lg text-warmBrown-900">Billing Address</h2>
                    <label className="flex items-center gap-2 cursor-pointer text-sm text-warmBrown-700 select-none">
                      <input
                        type="checkbox"
                        checked={billingSameAsShipping}
                        onChange={(e) => setBillingSameAsShipping(e.target.checked)}
                        className="w-4 h-4 rounded border-cream-300 text-rose-600 focus:ring-rose-500"
                      />
                      Same as shipping address
                    </label>
                  </div>

                  {billingSameAsShipping ? (
                    <div className="flex items-start gap-3 p-4 bg-cream-50 rounded-2xl border border-cream-200 text-sm text-warmBrown-600">
                      <CheckCircle className="w-5 h-5 text-rose-500 flex-shrink-0 mt-0.5" />
                      <p>
                        Your billing address will match your shipping address above. Uncheck the box
                        to enter a different billing address (the address associated with your payment card).
                      </p>
                    </div>
                  ) : (
                    <div className="space-y-5">
                      <p className="text-xs text-warmBrown-500 -mt-2">
                        Please enter the billing address exactly as it appears on your credit card statement.
                      </p>
                      <div className="grid grid-cols-2 gap-4">
                        <div>
                          <Label htmlFor="billing-first" className="text-sm font-medium text-warmBrown-700">First Name</Label>
                          <Input
                            id="billing-first"
                            value={billing.firstName}
                            onChange={(e) => setBilling({ ...billing, firstName: e.target.value })}
                            className="mt-2 rounded-2xl border-cream-200 focus:ring-rose-400 focus:border-rose-400"
                            required={!billingSameAsShipping}
                          />
                        </div>
                        <div>
                          <Label htmlFor="billing-last" className="text-sm font-medium text-warmBrown-700">Last Name</Label>
                          <Input
                            id="billing-last"
                            value={billing.lastName}
                            onChange={(e) => setBilling({ ...billing, lastName: e.target.value })}
                            className="mt-2 rounded-2xl border-cream-200 focus:ring-rose-400 focus:border-rose-400"
                            required={!billingSameAsShipping}
                          />
                        </div>
                      </div>
                      <div>
                        <Label htmlFor="billing-country" className="text-sm font-medium text-warmBrown-700">Country</Label>
                        <select
                          id="billing-country"
                          value={billingCountry}
                          onChange={(e) => setBillingCountry(e.target.value)}
                          className="mt-2 w-full px-4 py-3 rounded-2xl border border-cream-200 focus:outline-none focus:ring-2 focus:ring-rose-400 focus:border-rose-400 transition-all"
                          required={!billingSameAsShipping}
                        >
                          <option value="US">United States</option>
                          <option value="OTHER">Other</option>
                        </select>
                      </div>
                      <div>
                        <Label htmlFor="billing-address" className="text-sm font-medium text-warmBrown-700">Street Address</Label>
                        <Input
                          id="billing-address"
                          value={billing.address1}
                          onChange={(e) => setBilling({ ...billing, address1: e.target.value })}
                          className="mt-2 rounded-2xl border-cream-200 focus:ring-rose-400 focus:border-rose-400"
                          placeholder="123 Main Street"
                          required={!billingSameAsShipping}
                        />
                      </div>
                      <div>
                        <Label htmlFor="billing-address2" className="text-sm font-medium text-warmBrown-700">
                          Apartment, Suite, Unit <span className="text-warmBrown-400">(optional)</span>
                        </Label>
                        <Input
                          id="billing-address2"
                          value={billing.address2}
                          onChange={(e) => setBilling({ ...billing, address2: e.target.value })}
                          className="mt-2 rounded-2xl border-cream-200 focus:ring-rose-400 focus:border-rose-400"
                          placeholder="Apt 4B"
                        />
                      </div>
                      <div className="grid grid-cols-3 gap-4">
                        <div>
                          <Label htmlFor="billing-city" className="text-sm font-medium text-warmBrown-700">City</Label>
                          <Input
                            id="billing-city"
                            value={billing.city}
                            onChange={(e) => setBilling({ ...billing, city: e.target.value })}
                            className="mt-2 rounded-2xl border-cream-200 focus:ring-rose-400 focus:border-rose-400"
                            required={!billingSameAsShipping}
                          />
                        </div>
                        <div>
                          <Label htmlFor="billing-state" className="text-sm font-medium text-warmBrown-700">State</Label>
                          <Input
                            id="billing-state"
                            value={billing.state}
                            onChange={(e) => setBilling({ ...billing, state: e.target.value })}
                            className="mt-2 rounded-2xl border-cream-200 focus:ring-rose-400 focus:border-rose-400"
                            required={!billingSameAsShipping}
                          />
                        </div>
                        <div>
                          <Label htmlFor="billing-zip" className="text-sm font-medium text-warmBrown-700">ZIP</Label>
                          <Input
                            id="billing-zip"
                            value={billing.zip}
                            onChange={(e) => setBilling({ ...billing, zip: e.target.value })}
                            className="mt-2 rounded-2xl border-cream-200 focus:ring-rose-400 focus:border-rose-400"
                            required={!billingSameAsShipping}
                          />
                        </div>
                      </div>
                    </div>
                  )}
                </div>

                {/* Payment (NMI Collect.js hosted iframes) */}
                <div className="bg-white p-6 md:p-8 rounded-3xl shadow-card border border-cream-200">
                  <h2 className="font-display text-lg text-warmBrown-900 mb-6">Payment Details</h2>

                  {/* Card Logos */}
                  <div className="mb-6 p-4 bg-cream-50 rounded-2xl">
                    <p className="text-xs text-warmBrown-500 mb-3 flex items-center gap-2">
                      <CreditCard className="w-4 h-4" /> We Accept
                    </p>
                    <PaymentIcons size="md" />
                    <p className="mt-2 text-[11px] text-warmBrown-500">
                      {ACCEPTED_CARDS_LABEL}. <strong>Discover is not accepted.</strong>
                    </p>
                  </div>

                  <div className="space-y-5">
                    <div>
                      <Label className="text-sm font-medium text-warmBrown-700">Card Number</Label>
                      <div
                        id="ccnumber"
                        className="mt-2 rounded-2xl border border-cream-200 focus-within:ring-2 focus-within:ring-rose-400 focus-within:border-rose-400 px-4 h-12 bg-white transition-all"
                      />
                    </div>
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <Label className="text-sm font-medium text-warmBrown-700">Expiry Date</Label>
                        <div
                          id="ccexp"
                          className="mt-2 rounded-2xl border border-cream-200 focus-within:ring-2 focus-within:ring-rose-400 focus-within:border-rose-400 px-4 h-12 bg-white transition-all"
                        />
                      </div>
                      <div>
                        <Label className="text-sm font-medium text-warmBrown-700">CVV</Label>
                        <div
                          id="cvv"
                          className="mt-2 rounded-2xl border border-cream-200 focus-within:ring-2 focus-within:ring-rose-400 focus-within:border-rose-400 px-4 h-12 bg-white transition-all"
                        />
                      </div>
                    </div>
                  </div>

                  {/* Security Notice */}
                  <div className="flex items-center gap-2 mt-5 p-3 bg-sage-50 rounded-xl border border-sage-100">
                    <Lock className="w-4 h-4 text-sage-600" />
                    <span className="text-sage-700 text-sm font-medium">
                      Card data is encrypted and tokenized by our payment gateway. Your card number never touches our servers.
                    </span>
                  </div>

                  {!collectJsReady && (
                    <p className="mt-3 text-xs text-warmBrown-500">Loading secure card fields…</p>
                  )}

                  {paymentError && (
                    <div className="mt-4 flex items-start gap-2 text-red-600 text-sm p-3 bg-red-50 border border-red-200 rounded-xl">
                      <AlertCircle className="w-4 h-4 flex-shrink-0 mt-0.5" />
                      <span>{paymentError}</span>
                    </div>
                  )}
                </div>

                {/* Terms, Conditions & Policies */}
                <div className="bg-white p-6 md:p-8 rounded-3xl shadow-card border border-cream-200">
                  <h2 className="font-display text-lg text-warmBrown-900 mb-6">Terms & Policies</h2>

                  <div className="space-y-4">
                    <label className="flex items-start gap-3 cursor-pointer p-4 bg-cream-50 rounded-2xl border border-cream-200 hover:border-rose-200 transition-colors">
                      <input
                        type="checkbox"
                        checked={termsAccepted}
                        onChange={(e) => {
                          setTermsAccepted(e.target.checked);
                          if (e.target.checked) setTermsError(false);
                        }}
                        className="mt-1 w-5 h-5 rounded border-cream-300 text-rose-600 focus:ring-rose-500"
                      />
                      <span className="text-sm text-warmBrown-700">
                        <strong>Click Here to Accept:</strong> I have read and agree to the{' '}
                        <Link to="/terms" className="text-rose-600 hover:text-rose-700 underline" target="_blank">
                          Terms & Conditions
                        </Link>
                        {', '}
                        <Link to="/privacy" className="text-rose-600 hover:text-rose-700 underline" target="_blank">
                          Privacy Policy
                        </Link>
                        {', '}
                        <Link to="/returns" className="text-rose-600 hover:text-rose-700 underline" target="_blank">
                          Return & Refund Policy
                        </Link>
                        {', and '}
                        <Link to="/shipping" className="text-rose-600 hover:text-rose-700 underline" target="_blank">
                          Shipping Policy
                        </Link>
                        .
                      </span>
                    </label>
                    {termsError && (
                      <div className="flex items-center gap-2 text-red-600 text-sm px-4">
                        <AlertCircle className="w-4 h-4" />
                        <span>You must accept the Terms & Conditions to continue</span>
                      </div>
                    )}

                    <div className="p-4 bg-warmWhite-200 rounded-2xl space-y-4">
                      <div>
                        <h4 className="font-semibold text-warmBrown-800 text-sm mb-2">Order Authorization</h4>
                        <p className="text-xs text-warmBrown-600 leading-relaxed">
                          By clicking "Complete Order", you authorize a one-time charge of <strong>${total.toFixed(2)} {CURRENCY}</strong> for the products in your cart.
                          This amount includes product cost, shipping ({shippingCost === 0 ? 'FREE' : `$${shippingCost.toFixed(2)}`}), and applicable taxes (${tax.toFixed(2)}).
                        </p>
                      </div>

                      <div>
                        <h4 className="font-semibold text-warmBrown-800 text-sm mb-2">Shipping Information</h4>
                        <p className="text-xs text-warmBrown-600 leading-relaxed">
                          Orders are shipped via <strong>USPS</strong> (First Class or Priority Mail) within the United States.
                          Standard delivery: <strong>{SHIPPING_INFO.domestic.standard.deliveryTime}</strong>.
                          Express delivery: <strong>{SHIPPING_INFO.domestic.express.deliveryTime}</strong>.
                          Free shipping on orders over $99.
                        </p>
                      </div>

                      <div>
                        <h4 className="font-semibold text-warmBrown-800 text-sm mb-2">Return Policy Summary</h4>
                        <p className="text-xs text-warmBrown-600 leading-relaxed">
                          We offer a <strong>30-day return policy</strong> for unused items in original packaging.
                          See our full <Link to="/returns" className="text-rose-600 underline">Return & Refund Policy</Link> for details.
                        </p>
                      </div>

                      <div>
                        <h4 className="font-semibold text-warmBrown-800 text-sm mb-2">Billing Descriptor</h4>
                        <p className="text-xs text-warmBrown-600 leading-relaxed">
                          All charges from {BRAND_INFO.name} will appear on your credit or debit card statement
                          as &quot;<strong className="text-warmBrown-900">{BRAND_INFO.billingDescriptor}</strong>&quot;.
                          Please save this descriptor to help identify the charge and avoid chargebacks.
                        </p>
                      </div>

                      <div>
                        <h4 className="font-semibold text-warmBrown-800 text-sm mb-2">Customer Support</h4>
                        <p className="text-xs text-warmBrown-600 leading-relaxed">
                          Questions? Contact us at{' '}
                          <a href={`tel:${CONTACT_INFO.phone.replace(/[^0-9]/g, '')}`} className="text-rose-600">
                            {CONTACT_INFO.phone}
                          </a>
                          {' or '}
                          <a href={`mailto:${CONTACT_INFO.email}`} className="text-rose-600">
                            {CONTACT_INFO.email}
                          </a>
                        </p>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Place Order Button */}
                <button
                  type="submit"
                  disabled={isProcessing || countryError || !termsAccepted || !collectJsReady}
                  className="w-full py-4 bg-rose-600 hover:bg-rose-700 text-white font-semibold rounded-2xl transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2 shadow-lg shadow-rose-600/20"
                >
                  {isProcessing ? 'Processing Payment…' : `Complete Order — $${total.toFixed(2)} ${CURRENCY}`}
                </button>

                <div className="text-center">
                  <Link
                    to="/shop"
                    className="text-warmBrown-500 hover:text-rose-600 text-sm underline transition-colors"
                  >
                    Cancel and return to shopping
                  </Link>
                </div>
              </form>
            </div>

            {/* Order Summary */}
            <div className="lg:col-span-1">
              <div className="bg-white p-6 rounded-3xl shadow-card border border-cream-200 sticky top-[130px]">
                <h2 className="font-display text-lg text-warmBrown-900 mb-6">Order Summary</h2>

                <div className="space-y-4 mb-6">
                  {cartItems.map(item => (
                    <div key={item.id} className="flex gap-4">
                      <div className="w-16 h-16 bg-cream-100 rounded-xl flex-shrink-0 overflow-hidden">
                        <img src={item.image} alt={item.name} className="w-full h-full object-cover" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <h3 className="text-sm font-medium text-warmBrown-800 line-clamp-1">{item.name}</h3>
                        <p className="text-warmBrown-500 text-sm">Qty: {item.quantity}</p>
                      </div>
                      <p className="text-warmBrown-800 font-semibold">${(item.price * item.quantity).toFixed(2)}</p>
                    </div>
                  ))}
                </div>

                <div className="border-t border-cream-100 pt-4 space-y-3">
                  <div className="flex justify-between text-sm">
                    <span className="text-warmBrown-500">Subtotal</span>
                    <span className="text-warmBrown-800">${cartTotal.toFixed(2)}</span>
                  </div>
                  <div className="flex justify-between text-sm">
                    <span className="text-warmBrown-500">Shipping (USPS)</span>
                    <span className="text-warmBrown-800">{shippingCost === 0 ? 'Free' : `$${shippingCost.toFixed(2)}`}</span>
                  </div>
                  <div className="flex justify-between text-sm">
                    <span className="text-warmBrown-500">Tax</span>
                    <span className="text-warmBrown-800">${tax.toFixed(2)}</span>
                  </div>
                </div>

                <div className="border-t border-cream-100 mt-4 pt-4">
                  <div className="flex justify-between items-center">
                    <span className="font-display text-lg text-warmBrown-900">Total</span>
                    <span className="font-display text-2xl text-rose-600">${total.toFixed(2)} {CURRENCY}</span>
                  </div>
                </div>

                <div className="mt-6 pt-6 border-t border-cream-100 space-y-3">
                  <div className="flex items-center gap-2 text-sm text-warmBrown-600">
                    <Truck className="w-4 h-4 text-rose-500" />
                    {cartTotal >= 99 ? 'Free shipping on this order!' : `Add $${(99 - cartTotal).toFixed(2)} for free shipping`}
                  </div>
                  <div className="flex items-center gap-2 text-sm text-warmBrown-600">
                    <Shield className="w-4 h-4 text-rose-500" />
                    SSL Secured 256-bit Encryption
                  </div>
                  <div className="flex items-center gap-2 text-sm text-warmBrown-600">
                    <CheckCircle className="w-4 h-4 text-rose-500" />
                    30-Day Return Policy
                  </div>
                </div>

                <div className="mt-6 pt-6 border-t border-cream-100">
                  <p className="text-xs text-warmBrown-500 mb-2">Customer Service</p>
                  <a
                    href={`tel:${CONTACT_INFO.phone.replace(/[^0-9]/g, '')}`}
                    className="flex items-center gap-2 text-sm text-rose-600 hover:text-rose-700 transition-colors mb-1"
                  >
                    <Phone className="w-4 h-4" />
                    {CONTACT_INFO.phone}
                  </a>
                  <a
                    href={`mailto:${CONTACT_INFO.email}`}
                    className="flex items-center gap-2 text-sm text-rose-600 hover:text-rose-700 transition-colors"
                  >
                    <Mail className="w-4 h-4" />
                    {CONTACT_INFO.email}
                  </a>
                </div>
              </div>
            </div>
          </div>
        </div>
      </main>

      <Footer />
    </div>
  );
};

export default CheckoutPage;
